import { useCallback, useEffect, useMemo } from 'react';

import { PlusIcon, SettingsGearIcon, UpgradeChipIcon } from '@braskai/icons';
import { Chip, Icon, SpeakersPaletteTokens, Typography, useDeviceType } from '@braskai/ui-kit';
import { useNavigate, useParams } from 'react-router-dom';

import { Stack } from '@mui/material';

import {
  useCanCreateSpace,
  useCreateTeamspaceModal,
  useTeamspaceSettingsModal,
  useTeamspaceWelcomeModal,
} from '@/features/teamspace';
import { OnboardingTooltip, useOnboardingEvent } from '@/features/user/onboarding';

import { useBalanceStore, useUserBalanceStore } from '@/entities/balance';
import { useUpgradePlanModalStore } from '@/entities/pricing';
import { TeamspaceBanner, TeamspaceMenu, TeamspaceMenuItem, useGetSpaceById, useGetSpaces } from '@/entities/teamspace';
import { SIGNUP_SURVEY_ID, useCurrentUser, useSurvey } from '@/entities/user/currentUser';

import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PlanCategory } from '@/shared/types/Billing';
import { NavigationItem } from '@/shared/ui/NavigationItem';

type TeamspacesNavProps = {
  opened?: boolean;
  handleCloseSidebar?: () => void;
};

export const TeamspacesNav = ({ opened, handleCloseSidebar }: TeamspacesNavProps) => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { data: spaces } = useGetSpaces();
  const { data: currentSpace } = useGetSpaceById(spaceId || '');
  const { data: userId } = useCurrentUser((data) => data.id);
  const { isMobile } = useDeviceType();
  const { hasActiveSubscription, hasTrialPlan, planCategory } = useUserBalanceStore();
  const { isNewUser, showTooltip } = useTeamspaceWelcomeModal();
  const { isSurveyCompleted: isSignupSurveyCompleted } = useSurvey(SIGNUP_SURVEY_ID);
  const setOnboardingDiscoverTeamspaceEvent = useOnboardingEvent('discoverTeamspace_v1', true);
  const { hasActiveEnterprisePlan } = useBalanceStore();

  const spaceName = currentSpace?.name || 'My Teamspace';
  const isOwner = currentSpace?.owner_id === userId;
  const spaceColor = (currentSpace?.color as SpeakersPaletteTokens) || 'midnight';
  const canCreateSpace = useCanCreateSpace();

  const showTeamspaceBanner =
    !hasActiveSubscription ||
    hasTrialPlan ||
    planCategory === PlanCategory.Starter ||
    planCategory === PlanCategory.Creator;

  const openCreateTeamspaceModal = useCreateTeamspaceModal((state) => state.open);
  const openUpgradePlanModal = useUpgradePlanModalStore((state) => state.open);
  const openTeamspaceSettingsModal = useTeamspaceSettingsModal((state) => state.open);

  const handleUpgrade = useCallback(() => {
    openUpgradePlanModal({ limitedFeature: 'teamspace', plan: hasActiveEnterprisePlan ? 'custom' : 'enterprise' });
    handleCloseSidebar?.();
    MixpanelEvents.addMoreSpacesUpgradeClick();
  }, [handleCloseSidebar, hasActiveEnterprisePlan, openUpgradePlanModal]);

  const handleFreeUserUpgrade = useCallback(() => {
    openUpgradePlanModal({ limitedFeature: 'teamspace' });
    handleCloseSidebar?.();
    MixpanelEvents.teamSpaceUpgradeClick();
  }, [handleCloseSidebar, openUpgradePlanModal]);

  const handleSettingsClick = useCallback(() => {
    openTeamspaceSettingsModal();
    handleCloseSidebar?.();
  }, [handleCloseSidebar, openTeamspaceSettingsModal]);

  const handleCreateTeamspaceClick = useCallback(() => {
    openCreateTeamspaceModal();
    handleCloseSidebar?.();
    MixpanelEvents.createNewSpace();
  }, [handleCloseSidebar, openCreateTeamspaceModal]);

  const spaceOptions = useMemo(() => {
    const options = [
      ...(spaces?.map((space) => ({
        id: space.id,
        label: (
          <TeamspaceMenuItem
            name={space.name}
            color={(space.color as SpeakersPaletteTokens) || 'midnight'}
            isActive={space.id === spaceId}
            isMobile={isMobile}
            isOwner={space.owner_id === userId}
            membersCount={space.members.length + (space.invites?.length ?? 0)}
          />
        ),
        action: () => {
          navigate(`/spaces/${space.id}`);
        },
        disabled: !space.is_active && space.owner_id !== userId,
        isActive: space.id === spaceId,
      })) || [
        {
          id: 'mock_space',
          label: <TeamspaceMenuItem name="My Teamspace" color="midnight" membersCount={1} isActive isOwner />,
          disabled: false,
          isActive: true,
        },
      ]),
    ];

    if (!showTeamspaceBanner) {
      options.push({
        id: 'new-space',
        label: (
          <Stack
            alignItems="center"
            data-testid="new-teamspace-btn"
            direction="row"
            gap={4}
            justifyContent="space-between"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" gap={1}>
              <Icon color="grey.500" component={<PlusIcon />} size={16} />
              <Typography color="grey.900" variant="caption-1-md">
                New Teamspace
              </Typography>
            </Stack>

            {!canCreateSpace && (
              <UpgradeChipIcon
                style={{
                  borderRadius: 4,
                  height: 20,
                  width: 20,
                }}
              />
            )}
          </Stack>
        ),
        action: canCreateSpace ? handleCreateTeamspaceClick : handleUpgrade,

        disabled: false,
        isActive: false,
      });
    }

    return options;
  }, [
    spaces,
    showTeamspaceBanner,
    userId,
    spaceId,
    isMobile,
    navigate,
    handleCreateTeamspaceClick,
    handleUpgrade,
    canCreateSpace,
  ]);

  useEffect(() => {
    if (isSignupSurveyCompleted && !isNewUser) {
      setOnboardingDiscoverTeamspaceEvent();
    }
  }, [isSignupSurveyCompleted, isNewUser, setOnboardingDiscoverTeamspaceEvent]);

  if (isMobile) {
    return (
      <Stack direction="row" gap={8} height={44}>
        <TeamspaceMenu name={spaceName} isOwner={isOwner} options={spaceOptions} sx={{ minWidth: 0 }} />
        <NavigationItem
          sx={{
            flexShrink: 0,
            marginBottom: 0,
            marginTop: 0,
            width: 44,
          }}
          component="div"
          icon={<SettingsGearIcon height={20} width={20} />}
          isMobile
          isSquare
          onClick={handleSettingsClick}
        />
      </Stack>
    );
  }

  return (
    <OnboardingTooltip
      open={isNewUser ? showTooltip : isSignupSurveyCompleted}
      stepName={isNewUser ? 'manageTeamspace_v1' : 'discoverTeamspace_v1'}
    >
      <div>
        <TeamspaceMenu
          name={spaceName}
          bottomBanner={
            showTeamspaceBanner && (
              <TeamspaceBanner
                dataTestId="free-plan-teamspace-banner"
                description="Make teamwork easier with Teamspaces"
                isImage
                onClick={handleFreeUserUpgrade}
              >
                <Chip label="Upgrade" color="primary" isColoredBg={false} size="md" variant="squared" />
              </TeamspaceBanner>
            )
          }
          color={spaceColor}
          isOpenSidebar={opened}
          isOwner={isOwner}
          options={spaceOptions}
        />
      </div>
    </OnboardingTooltip>
  );
};
